import Phaser from 'phaser';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { SfxManager } from '../utils/SfxManager';
import { ResourceBanner } from '../ui/ResourceBanner';
import { BUILDING_DEFINITIONS } from '../data/buildings';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import { SpriteRenderer } from '../rendering/SpriteRenderer';
import { MusicManager } from '../systems/MusicManager';
import { TutorialOverlay } from '../ui/TutorialOverlay';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE DU CAMP ===
// Hub central entre les runs. Accès au coffre, dortoir, atelier, vigie, et départ en run.

export class CampScene extends Phaser.Scene {
  private camp!: CampManager;
  private meta!: MetaManager;
  private sfx = new SfxManager();

  constructor() {
    super({ key: 'CampScene' });
  }

  private firstVisit = false;

  init(data: { camp?: CampManager; meta?: MetaManager; firstVisit?: boolean }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
    this.firstVisit = data?.firstVisit ?? false;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Fond
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0f1520);
    this.createCampAtmosphere();

    // Bandeau ressources
    new ResourceBanner(this, this.camp.getResources());
    // Musique du camp
    MusicManager.crossfadeTo(this, 'camp');

    // Titre
    this.add.text(GAME_CX, 35, '🏴‍☠️ CAMP DES PIRATES', {
      fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    // Description ambiance
    this.add.text(GAME_CX, 65, 'Votre repaire sur l\'île. Préparez-vous avant de repartir.', {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Infos rapides
    const survivors = this.camp.getAliveSurvivors();
    const storage = this.camp.getStorage();
    const stats = this.meta.getStats();

    this.add.text(GAME_CX, 95, `👥${survivors.length}  📦${storage.length}/${this.camp.getStorageCapacity()}  🏆${stats.runsWon}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // === ZONES DU CAMP ===
    // Sprint 78 : 7 zones (ajout Taverne)
    // Bug fix : zoneY abaissé à 150 pour ne pas couvrir le bandeau "Construction" (y=115).
    // Hauteur d'une zone = 62px centrée à y+13 (drawZone). Spacing >= 62 sinon chevauchement.
    // Spacing 64 = 2px d'écart visible entre tuiles. 150 + 64*6 = 534, bottom 534+44=578.
    // RunBtn à y=600 → marge 22px ✓
    const zoneY = 150;
    const spacing = 64;

    // Construction en cours
    const construction = this.camp.getUpgrades().getConstruction();
    if (construction) {
      const cDef = BUILDING_DEFINITIONS[construction.buildingId];
      this.add.text(GAME_CX, 115, `🏗️ Construction : ${cDef.emoji} ${cDef.name} → Niv.${construction.targetLevel}`, {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
    }

    // Coffre
    this.drawZone(GAME_CX, zoneY, '📦 Coffre', 'Stocker et récupérer de l\'équipement', `${storage.length}/${this.camp.getStorageCapacity()} items`, () => {
      this.sfx.select();
      startSceneWithFade(this, 'StorageScene', { camp: this.camp, meta: this.meta });
    }, 'building-coffre');

    // Dortoir
    this.drawZone(GAME_CX, zoneY + spacing, '🛏️ Dortoir', 'Voir vos survivants', `${survivors.length}/${this.camp.getSurvivorCapacity()} pirate(s)`, () => {
      this.sfx.select();
      startSceneWithFade(this, 'DormScene', { camp: this.camp, meta: this.meta });
    }, 'building-dortoir');

    // Atelier
    const workshopLvl = this.camp.getUpgrades().getBuildingLevel('workshop');
    this.drawZone(GAME_CX, zoneY + spacing * 2, '🔨 Atelier', 'Craft et upgrades de bâtiments', `Niv.${workshopLvl}`, () => {
      this.sfx.select();
      startSceneWithFade(this, 'AtelierScene', { camp: this.camp, meta: this.meta });
    }, 'building-atelier');

    // Vigie
    const vigieLevel = this.camp.getUpgrades().getBuildingLevel('watchtower');
    const expeditionMgr = new ExpeditionManager();
    const vigieInfo = vigieLevel === 0 ? 'Non construite' : expeditionMgr.isOnExpedition() ? '⏳ En cours' : `Niv.${vigieLevel}`;
    this.drawZone(GAME_CX, zoneY + spacing * 3, '🗼 Vigie', 'Envoyer des survivants en expédition', vigieInfo, () => {
      this.sfx.select();
      startSceneWithFade(this, 'VigieScene', { camp: this.camp, meta: this.meta });
    }, 'building-vigie');

    // Taverne (Sprint 78) — Liar's Dice contre PNJ pirates
    const tavernLvl = this.camp.getUpgrades().getBuildingLevel('tavern');
    const tavernInfo = tavernLvl === 0 ? 'Non construite' : `Niv.${tavernLvl}`;
    this.drawZone(GAME_CX, zoneY + spacing * 4, '🍺 Taverne', 'Jeu de dés des menteurs', tavernInfo, () => {
      this.sfx.select();
      startSceneWithFade(this, 'TavernScene', { camp: this.camp, meta: this.meta });
    });

    // Stats & Succès
    this.drawZone(GAME_CX, zoneY + spacing * 5, '📊 Statistiques', 'Voir vos stats et succès', `${stats.enemiesKilled} kills`, () => {
      this.sfx.select();
      startSceneWithFade(this, 'StatsScene', { meta: this.meta, fromCamp: true, camp: this.camp });
    });

    // Codex
    this.drawZone(GAME_CX, zoneY + spacing * 6, '📖 Codex', 'Encyclopédie des ennemis et items', '', () => {
      this.sfx.select();
      startSceneWithFade(this, 'CodexScene', { returnTo: 'CampScene', camp: this.camp, meta: this.meta });
    });

    // === DÉPART EN RUN ===
    const runBtn = this.add.text(GAME_CX, 600, '[ ⚔️ Partir en Expédition ]', {
      fontSize: '20px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 24, y: 12 },
    }).setOrigin(0.5);
    runBtn.setInteractive({ useHandCursor: true });
    runBtn.on('pointerdown', () => {
      this.sfx.select();
      this.startRun();
    });
    runBtn.on('pointerover', () => runBtn.setColor('#27ae60'));
    runBtn.on('pointerout', () => runBtn.setColor('#2ecc71'));

    // Tutoriel première visite (étape par étape)
    if (this.firstVisit && !TutorialOverlay.isDone()) {
      const tuto = new TutorialOverlay(this, [
        {
          text: '🏴‍☠️ Bienvenue sur Deadtide, Capitaine !\n\nVous venez d\'échouer sur l\'Archipel Maudit.\nCe camp est votre base entre les expéditions.\nLa mort en expédition est définitive !',
          x: GAME_CX, y: 200,
        },
        {
          text: '📦 Le Coffre stocke votre équipement.\nVous y trouverez un coutelas et des bottes\npour commencer. Récupérez-les avant\nvotre première expédition !',
          x: GAME_CX, y: 200,
          arrowTo: { x: GAME_CX, y: zoneY + 15 },
        },
        {
          text: '🛏️ Le Dortoir liste vos survivants.\nRecrutez des alliés pour agrandir\nvotre équipe (jusqu\'à 3 membres) !\n⚔️ Guerrier  🏹 Tireur  💊 Médecin',
          x: GAME_CX, y: 200,
          arrowTo: { x: GAME_CX, y: zoneY + spacing + 15 },
        },
        {
          text: '🔨 L\'Atelier permet de crafter des objets\net d\'améliorer vos 5 bâtiments avec\nles ressources récoltées en expédition.\nCommencez par upgrader le Coffre ou le Dortoir !',
          x: GAME_CX, y: 200,
          arrowTo: { x: GAME_CX, y: zoneY + spacing * 2 + 15 },
        },
        {
          text: '🗼 La Vigie envoie vos survivants\nen expédition idle pendant que vous jouez.\nRisque de mort mais bonnes récompenses !\n(Construisez-la à l\'Atelier d\'abord)',
          x: GAME_CX, y: 200,
          arrowTo: { x: GAME_CX, y: zoneY + spacing * 3 + 15 },
        },
        {
          text: '⚔️ Cliquez sur "Partir en Expédition"\npour équiper votre équipe et explorer\nl\'archipel. Cherchez les points ⛵ Extraction\npour rentrer avec votre butin !',
          x: GAME_CX, y: 370,
          arrowTo: { x: GAME_CX, y: 590 },
        },
      ], () => { this.firstVisit = false; });
      tuto.start();
    }

    // Retour menu
    const menuBtn = this.add.text(15, 10, '← Menu', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 10, y: 6 },
    });
    menuBtn.setInteractive({ useHandCursor: true });
    menuBtn.on('pointerdown', () => startSceneWithFade(this, 'BootScene'));
    menuBtn.on('pointerover', () => menuBtn.setColor('#bdc3c7'));
    menuBtn.on('pointerout', () => menuBtn.setColor('#7f8c8d'));

    // Sprint A4 final — bouton « Île » pour revenir au camp pixel art si l'utilisateur
    // est arrivé ici par l'escape hatch du panneau d'affichage. Affiché uniquement si
    // le flag est activé (cas normal depuis v0.40.6 — par défaut l'île remplace ce
    // CampScene, et ce CampScene n'est consulté qu'en escape hatch ou avec le flag off).
    if (IslandCampFlag.isEnabled()) {
      const islandBtn = this.add.text(GAME_W - 15, 10, '🏝️ Île →', {
        fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2e1a', padding: { x: 10, y: 6 },
      }).setOrigin(1, 0);
      islandBtn.setInteractive({ useHandCursor: true });
      islandBtn.on('pointerdown', () =>
        startSceneWithFade(this, 'IslandCampScene', { camp: this.camp, meta: this.meta }));
      islandBtn.on('pointerover', () => islandBtn.setColor('#27ae60'));
      islandBtn.on('pointerout', () => islandBtn.setColor('#2ecc71'));
    }
  }

  /**
   * Atmosphère du camp : braises orangées qui montent (effet feu de camp).
   * Couches en depth -10/-9 pour rester derrière l'UI.
   */
  private createCampAtmosphere(): void {
    // Brume violacée en bas (vignette inversée — sol)
    this.add.rectangle(GAME_CX, GAME_H - 60, GAME_W, 120, 0x1a0a1a, 0.35).setDepth(-10);

    // Braises montant lentement
    for (let i = 0; i < 14; i++) {
      const x = Phaser.Math.Between(15, GAME_W - 15);
      const startY = Phaser.Math.Between(GAME_H * 0.6, GAME_H);
      const ember = this.add.circle(x, startY, Phaser.Math.Between(1, 2), 0xff8c2a, 0.7).setDepth(-9);
      this.tweens.add({
        targets: ember,
        y: -10,
        alpha: 0,
        duration: Phaser.Math.Between(5000, 8500),
        delay: Phaser.Math.Between(0, 4000),
        repeat: -1,
        onRepeat: () => {
          ember.y = Phaser.Math.Between(GAME_H * 0.6, GAME_H);
          ember.alpha = 0.7;
          ember.x = Phaser.Math.Between(15, GAME_W - 15);
        },
      });
    }
  }

  private drawZone(x: number, y: number, title: string, desc: string, info: string, onClick: () => void, iconId?: string): void {
    // Fond de zone — clampé à la largeur du canvas (mobile 450 / desktop 800 → 600 inchangé)
    const zoneW = Math.min(600, GAME_W - 20);
    const bg = this.add.rectangle(x, y + 13, zoneW, 62, 0x1a2540);
    bg.setStrokeStyle(1, 0x2a3a5c);

    // Icône SVG du bâtiment (si disponible) — remplace l'emoji du titre
    let displayTitle = title;
    let hasIcon = false;
    if (iconId) {
      const iconKey = SpriteRenderer.iconKey(iconId);
      if (this.textures.exists(iconKey)) {
        hasIcon = true;
        // Retirer l'emoji du titre
        displayTitle = title.replace(/^[^\s]+\s/, '');
      }
    }

    const titleText = this.add.text(x, y, displayTitle, {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    // Placer l'icône juste à gauche du titre
    if (hasIcon && iconId) {
      const iconKey = SpriteRenderer.iconKey(iconId);
      const titleWidth = titleText.width;
      this.add.sprite(x - titleWidth / 2 - 14, y + 2, iconKey).setScale(1.2);
    }

    this.add.text(x, y + 18, desc, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    if (info) {
      this.add.text(x, y + 34, info, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
    }

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => {
      bg.setStrokeStyle(2, 0xf1c40f);
      titleText.setColor('#f1c40f');
    });
    bg.on('pointerout', () => {
      bg.setStrokeStyle(1, 0x2a3a5c);
      titleText.setColor('#ecf0f1');
    });
    bg.on('pointerdown', onClick);
  }

  private startRun(): void {
    startSceneWithFade(this, 'RunPrepScene', { camp: this.camp, meta: this.meta });
  }
}
